import React, { useState, useEffect } from 'react';
import PublicLayout from '../PublicLayout';
import { Calendar, Users, MapPin, Search, X } from 'lucide-react';

const PhotoGallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  // Fetch photos from backend
  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE}/photo-gallery/public`);
        const data = await response.json();

        if (data.success) {
          // Backend already filters for active photos
          setGalleryImages(data.data);
        } else {
          setError('Failed to load gallery photos');
        }
      } catch (err) {
        console.error('Error fetching gallery photos:', err);
        setError('Failed to load gallery photos');
      } finally {
        setLoading(false);
      }
    };

    fetchPhotos();
  }, [API_BASE]);

  const categories = ['All', 'Events', 'Training', 'Leadership', 'Partnerships', 'Community', 'Equipment', 'Research', 'Chapters'];

  const filteredImages = selectedCategory === 'All'
    ? galleryImages
    : galleryImages.filter(image => image.category === selectedCategory);

  const openModal = (image) => {
    setSelectedImage(image);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id);
    const nextIndex = (currentIndex + 1) % filteredImages.length;
    setSelectedImage(filteredImages[nextIndex]);
  };

  const prevImage = () => {
    const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id);
    const prevIndex = (currentIndex - 1 + filteredImages.length) % filteredImages.length;
    setSelectedImage(filteredImages[prevIndex]);
  };

  return (
    <PublicLayout>
      <div className="py-5" style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)' }}>
        <div className="container">
          {/* Header */}
          <div className="text-center mb-5">
            <h1 className="display-4 fw-bold text-dark mb-4">Photo & Event Gallery</h1>
            <p className="lead text-muted">
              Capturing moments from ARCA events, training sessions, and collaborative activities across Africa
            </p>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">Loading gallery photos...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="alert alert-warning text-center" role="alert">
              {error}
            </div>
          )}

          {/* Gallery Content */}
          {!loading && !error && (
            <>
              {/* Category Filter */}
              <div className="row justify-content-center mb-5">
                <div className="col-lg-8">
                  <div className="d-flex flex-wrap justify-content-center gap-2">
                    {categories.map(category => (
                      <button
                        key={category}
                        onClick={() => setSelectedCategory(category)}
                        className={`btn ${selectedCategory === category
                          ? 'btn-primary'
                          : 'btn-outline-primary'} btn-sm`}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Gallery Grid */}
              <div className="row g-4">
                {filteredImages.length === 0 ? (
                  <div className="col-12 text-center py-5">
                    <p className="text-muted">No photos found in this category.</p>
                  </div>
                ) : (
                  filteredImages.map(image => (
                    <div key={image.id} className="col-lg-4 col-md-6">
                      <div
                        className="card h-100 shadow-sm border-0 overflow-hidden"
                        style={{ cursor: 'pointer' }}
                        onClick={() => openModal(image)}
                      >
                        <div className="position-relative" style={{ height: '250px' }}>
                          <img
                            src={image.image_path?.startsWith('/uploads/') ? `${BACKEND_URL}${image.image_path}` : image.image_path}
                            alt={image.title}
                            className="w-100 h-100 object-fit-cover"
                            style={{ transition: 'transform 0.3s' }}
                            onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                            onError={(e) => {
                              e.target.onerror = null;
                              console.error('Failed to load gallery image:', e.target.src);
                            }}
                          />
                          <div className="position-absolute top-0 end-0 m-2">
                            <span className="badge bg-primary">{image.category}</span>
                          </div>
                        </div>
                        <div className="card-body p-3">
                          <h5 className="card-title text-dark mb-2">{image.title}</h5>
                          <p className="card-text text-muted small mb-2">{image.description || 'No description available'}</p>
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center text-muted small">
                              <Calendar size={14} className="me-1" />
                              <span>{new Date(image.date).toLocaleDateString()}</span>
                            </div>
                            <div className="d-flex align-items-center text-muted small">
                              <MapPin size={14} className="me-1" />
                              <span>{image.location}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          {/* Modal */}
          {selectedImage && (
            <div
              className="modal fade show d-block"
              style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}
              onClick={closeModal}
            >
              <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h5 className="modal-title">{selectedImage.title}</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={closeModal}
                    ></button>
                  </div>
                  <div className="modal-body p-0">
                    <img
                      src={selectedImage.image_path?.startsWith('/uploads/') ? `${BACKEND_URL}${selectedImage.image_path}` : selectedImage.image_path}
                      alt={selectedImage.title}
                      className="w-100"
                      style={{ maxHeight: '70vh', objectFit: 'contain' }}
                      onError={(e) => {
                        e.target.onerror = null;
                        console.error('Failed to load gallery image:', e.target.src);
                      }}
                    />
                  </div>
                  <div className="modal-footer">
                    <div className="w-100">
                      <p className="mb-2">{selectedImage.description || 'No description available'}</p>
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center text-muted">
                          <Calendar size={16} className="me-2" />
                          <span>{new Date(selectedImage.date).toLocaleDateString()}</span>
                          <MapPin size={16} className="ms-3 me-2" />
                          <span>{selectedImage.location}</span>
                        </div>
                        <div>
                          <button
                            className="btn btn-outline-primary btn-sm me-2"
                            onClick={prevImage}
                          >
                            Previous
                          </button>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={nextImage}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}


        </div>
      </div>
    </PublicLayout>
  );
};

export default PhotoGallery;